import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Page } from '../../types';
import { useCollection } from '../../hooks/useCollection';
import { doc, deleteDoc, updateDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../firebase';
import ConfirmModal from '../../components/ConfirmModal';
import { useToast } from '../../hooks/useToast';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const PlusIcon: React.FC<{ className?: string }> = ({ className }) => (<svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>);
const MenuIcon: React.FC<{ className?: string }> = ({ className }) => (<svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>);

const ToggleSwitch: React.FC<{ enabled: boolean, onChange: () => void }> = ({ enabled, onChange }) => (
    <button type="button" className={`${enabled ? 'bg-gray-800' : 'bg-gray-300'} relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none`} onClick={onChange} aria-checked={enabled} role="switch">
        <span className={`${enabled ? 'translate-x-6' : 'translate-x-1'} inline-block w-4 h-4 transform bg-white rounded-full transition-transform`} />
    </button>
);

const SortablePageItem: React.FC<{ page: Page, onToggle: (page: Page) => void, onDelete: (page: Page) => void }> = ({ page, onToggle, onDelete }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: page.id });
    const style = { transform: CSS.Transform.toString(transform), transition };

    return (
        <tr ref={setNodeRef} style={style}>
            <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm w-12">
                <button {...attributes} {...listeners} className="cursor-grab p-2 text-gray-400 hover:text-gray-700">
                    <MenuIcon className="h-5 w-5" />
                </button>
            </td>
            <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                <Link to={`/admin/page/edit/${page.id}`} className="text-gray-900 hover:text-indigo-600 whitespace-no-wrap font-semibold">
                    {page.title}
                </Link>
                <p className="text-gray-600 whitespace-no-wrap text-xs">/page/{page.slug}</p>
            </td>
            <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                <p className="text-gray-900 whitespace-no-wrap capitalize">{page.layoutTemplate}</p>
            </td>
            <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm text-center">
                <ToggleSwitch enabled={page.published} onChange={() => onToggle(page)} />
            </td>
            <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm text-right whitespace-no-wrap">
                <Link to={`/admin/page/edit/${page.id}`} className="text-indigo-600 hover:text-indigo-900 mr-4">Edit</Link>
                <button onClick={() => onDelete(page)} className="text-red-600 hover:text-red-900">Delete</button>
            </td>
        </tr>
    );
};

const AdminPagesPage: React.FC = () => {
    const { data: pages, setData: setPages, isLoading } = useCollection<Page>('pages', { field: 'order', direction: 'asc' });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [pageToDelete, setPageToDelete] = useState<Page | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const { showToast } = useToast();

    const openDeleteModal = (page: Page) => {
        setPageToDelete(page);
        setIsModalOpen(true);
    };

    const handleDelete = async () => {
        if (!pageToDelete) return;
        setIsDeleting(true);
        try {
            await deleteDoc(doc(db, 'pages', pageToDelete.id));
            setPages(prev => prev.filter(p => p.id !== pageToDelete.id));
            showToast('Page deleted successfully.', { type: 'success' });
        } catch (error) {
            showToast("Error: Could not delete the page.", { type: 'error' });
        } finally {
            setIsModalOpen(false);
            setPageToDelete(null);
            setIsDeleting(false);
        }
    };

    const handleTogglePublished = async (page: Page) => {
        const updatedPage = { ...page, published: !page.published };
        await updateDoc(doc(db, 'pages', page.id), { published: updatedPage.published });
        setPages(prev => prev.map(p => p.id === page.id ? updatedPage : p));
    };

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (pages && over && active.id !== over.id) {
            const oldIndex = pages.findIndex((p) => p.id === active.id);
            const newIndex = pages.findIndex((p) => p.id === over.id);
            const newOrderPages = arrayMove(pages, oldIndex, newIndex);
            
            setPages(newOrderPages);

            try {
                const batch = writeBatch(db);
                newOrderPages.forEach((page, index) => {
                    const pageRef = doc(db, 'pages', page.id);
                    batch.update(pageRef, { order: index });
                });
                await batch.commit();
                showToast('Page order updated.', { type: 'success' });
            } catch (error) {
                showToast('Failed to update page order.', { type: 'error' });
                // Revert optimistic update on failure
                setPages(pages);
            }
        }
    };

    return (
        <div>
            <ConfirmModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onConfirm={handleDelete} title="Delete Page" description={`Are you sure you want to delete the page "${pageToDelete?.title}"? This action cannot be undone.`} isConfirming={isDeleting} />
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-gray-800">Manage Pages</h2>
                <Link to="/admin/page/new" className="inline-flex items-center px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition-colors">
                    <PlusIcon className="h-5 w-5 mr-2" />Add New Page
                </Link>
            </div>
            <div className="bg-white shadow-md rounded-lg overflow-x-auto">
                {isLoading && <p className="p-6 text-center">Loading pages...</p>}
                {!isLoading && (
                    <table className="min-w-full leading-normal">
                        <thead>
                            <tr>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 w-12"></th>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Page Title</th>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Template</th>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Published</th>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext items={pages?.map(p => p.id) || []} strategy={verticalListSortingStrategy}>
                                <tbody>
                                    {(pages || []).map(page => (
                                        <SortablePageItem key={page.id} page={page} onToggle={handleTogglePublished} onDelete={openDeleteModal} />
                                    ))}
                                </tbody>
                            </SortableContext>
                        </DndContext>
                    </table>
                )}
                {!isLoading && pages && pages.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                        <p>No pages found.</p>
                        <p className="mt-2"><Link to="/admin/page/new" className="text-indigo-600 hover:underline">Create your first one!</Link></p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminPagesPage;